import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {
  fontFamily,
  reviewScreenButtonWithColor,
  reviewScreenButtonWithOutColor,
} from "../Constants/constants";
import Autocomplete from "@mui/material/Autocomplete";
import { BASE_URL } from "../../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { checkIsSuperAdmin } from "../../Utility/utility";

export default function BusinessModalComponent({ show, handleClose, onSave, editingData, title }) {
  const [businessUnit, setBusinessUnit] = React.useState("");
  const [businessId, setBusinessId] = React.useState("");
  const [isDefaultChecked, setIsDefaultChecked] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const currentTimestamp = new Date();
  const seconds = currentTimestamp.getSeconds();
  const millSeconds = currentTimestamp.getMilliseconds();
  const formattedTimestamp = `${currentTimestamp.getFullYear()}-${(currentTimestamp.getMonth() + 1).toString().padStart(2, "0")}-${currentTimestamp.getDate().toString().padStart(2, "0")} ${currentTimestamp.getHours().toString().padStart(2, "0")}:${currentTimestamp.getMinutes().toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  useEffect(() => {
    const checkAdminStatus = async () => {
      const isSuperAdminUser = await checkIsSuperAdmin();
      setIsSuperAdmin(isSuperAdminUser);

      if (isSuperAdminUser) {
        // Fetch organizations for super admin
        const orgResponse = await fetch(
          `${BASE_URL}/Organization/${getUserIdFromLocalStorage().userId}/${getUserIdFromLocalStorage().organizationId}`,
          {
            headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
          }
        );
        if (orgResponse.ok) {
          const orgData = await orgResponse.json();
          const validOrgData = orgData.filter((org) => org && org.id && org.name);
          setOrganizations(validOrgData.reverse());
        }
      } else {
        // Fetch users for admin
        const userDataJSON = localStorage.getItem("token");
        if (userDataJSON) {
          const userData = JSON.parse(userDataJSON);
          const usersResponse = await fetch(
            `${BASE_URL}/users/getAllUsers/${userData.organizationId}`,
            {
              headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
            }
          );
          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            setUsers(usersData.filter((user) => user.isDeleted === false));
          }
        }
      }
    };

    checkAdminStatus();
  }, []);

  React.useEffect(() => {
    if (editingData) {
      console.log(editingData);
      setBusinessUnit(editingData.businessUnit);
      setBusinessId(editingData.id);
      setIsDefaultChecked(editingData.isDefault);

      // Set organization/user if editing
      if (isSuperAdmin && editingData.organizationId) {
        const org = organizations.find((o) => o.id === editingData.organizationId);
        if (org) setSelectedOrganization(org);
      } else if (!isSuperAdmin && editingData.userId) {
        const user = users.find((u) => u.id === editingData.userId);
        if (user) setSelectedUser(user);
      }
    } else {
      setBusinessUnit("");
      setBusinessId(formattedTimestamp);
      setIsDefaultChecked(false);
      setSelectedOrganization(null);
      setSelectedUser(null);
    }
  }, [editingData, organizations, users, isSuperAdmin]);

  const handleSaveClick = () => {
    const newRow = {
      id: businessId,
      businessUnit,
      isDefaultChecked,
      organizationId: isSuperAdmin ? selectedOrganization?.id : null,
      userId: !isSuperAdmin ? selectedUser?.id : null,
    };
    onSave(newRow);
  };

  const handleCheckboxChange = (event) => {
    setIsDefaultChecked(event.target.checked);
  };

  const borderStyle = {
    width: "250px",
    backgroundColor: "white",
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2aa7e0",
    },
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2aa7e0",
    },
    "&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2aa7e0",
    },
  };

  const labelStyle = {
    color: "black",
    display: "inline-block",
    width: "120px",
    fontFamily: fontFamily,
  };

  const fieldContainerStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
  };

  return (
    <Modal
      open={show}
      onClose={handleClose}
      aria-labelledby="add-administrator-modal-title"
      aria-describedby="add-administrator-modal-description"
    >
      <div
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
          width: "450px",
          padding: "20px",
          outline: "none",
          backgroundColor: "white",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
        }}
      >
        <h6 style={{ color: "#2aa7e0", fontFamily: fontFamily }}>{title}</h6>
        <div
          style={{
            height: "1px",
            width: "410px",
            backgroundColor: "#2aa7e0",
            marginBottom: "20px",
            marginRight: "10px",
          }}
        ></div>

        <div>
          <div style={fieldContainerStyle}>
            <span style={labelStyle}>Client</span>
            <TextField
              value={businessUnit}
              sx={borderStyle}
              onChange={(e) => setBusinessUnit(e.target.value)}
              variant="outlined"
              inputProps={{ style: { padding: "2px 0 2px 6px" } }}
              style={{ backgroundColor: "white", border: "0px", fontFamily: fontFamily }}
              disabled={false}
            />
          </div>

          {isSuperAdmin && (
            <div style={fieldContainerStyle}>
              <span style={labelStyle}>Organization</span>
              <Autocomplete
                options={organizations}
                getOptionLabel={(option) => option.name || ""}
                value={selectedOrganization}
                onChange={(event, newValue) => {
                  setSelectedOrganization(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={borderStyle}
                    style={{ backgroundColor: "white", border: "0px", fontFamily: fontFamily }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No organizations found"
                style={{ width: "250px" }}
              />
            </div>
          )}

          {!isSuperAdmin && (
            <div style={fieldContainerStyle}>
              <span style={labelStyle}>User</span>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}` || ""}
                value={selectedUser}
                onChange={(event, newValue) => {
                  setSelectedUser(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={borderStyle}
                    style={{ backgroundColor: "white", border: "0px", fontFamily: fontFamily }}
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      display: "flex",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      padding: "8px 16px",
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: "14px" }}
                      >{`${option.firstName} ${option.lastName}`}</div>
                      <div style={{ fontSize: "12px", color: "#666" }}>{option.email}</div>
                    </div>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No users found"
                style={{ width: "250px" }}
              />
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button
              variant="contained"
              style={reviewScreenButtonWithOutColor(100)}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              style={reviewScreenButtonWithColor(80)}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

BusinessModalComponent.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingData: PropTypes.object,
  title: PropTypes.string.isRequired,
};
